import React, { useEffect, useState } from 'react';
import FormComponent from '../forms/index';
import { register, validateInvitation, completeInvitation, InvitationValidationResponse } from '../../services/Authentication';
import { useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { useBoundStore } from '../../store';
import { getOwnUser } from '../../services/User';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LinkIcon, Clock } from "lucide-react";
import logo from '../../assets/logo.png';

// Matches the backend's @MinLength on the password
const MIN_PASSWORD_LENGTH = 8;

const RegisterPage: React.FC = () => {
  const { signUpCode } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const setAccessToken = useBoundStore((state) => state.setAccessToken);
  const setRefreshToken = useBoundStore((state) => state.setRefreshToken);
  const setUser = useBoundStore((state) => state.setUser);

  const [validationState, setValidationState] = useState<{
    loading: boolean;
    valid: boolean | null;
    error: string | null;
    errorType?: 'expired' | 'used' | 'invalid';
    organizationName?: string;
    /** Address the invitation was sent to: the account is created for it */
    email?: string;
    isLegacyCode: boolean;
  }>({
    loading: true,
    valid: null,
    error: null,
    isLegacyCode: false,
  });

  useEffect(() => {
    const checkInvitation = async () => {
      if (!signUpCode) {
        setValidationState({
          loading: false,
          valid: false,
          error: t.auth.errors.noInvitationCode,
          errorType: 'invalid',
          isLegacyCode: false,
        });
        return;
      }

      // Try new invitation flow first
      const response = await validateInvitation(signUpCode);

      if (response.success && response.data) {
        const data = response.data as InvitationValidationResponse;
        if (data.valid) {
          setValidationState({
            loading: false,
            valid: true,
            error: null,
            organizationName: data.organizationName,
            email: data.email,
            isLegacyCode: false,
          });
        } else if (data.expired) {
          setValidationState({
            loading: false,
            valid: false,
            error: t.auth.errors.invitationExpired,
            errorType: 'expired',
            isLegacyCode: false,
          });
        } else if (data.used) {
          setValidationState({
            loading: false,
            valid: false,
            error: t.auth.errors.invitationUsed,
            errorType: 'used',
            isLegacyCode: false,
          });
        } else {
          // Not found in new system, could be legacy signup code
          setValidationState({
            loading: false,
            valid: true,
            error: null,
            isLegacyCode: true,
          });
        }
      } else {
        // API error or not found - try legacy flow
        setValidationState({
          loading: false,
          valid: true,
          error: null,
          isLegacyCode: true,
        });
      }
    };

    checkInvitation();
  }, [signUpCode, t]);

  // Legacy registration handler (existing flow)
  const handleLegacyRegisterSubmit = async (formData: Record<string, any>) => {
    setAccessToken("");
    setRefreshToken("");
    setUser(null);
    if (!signUpCode)
      return { success: false, errors: [{ title: t.auth.errors.signUpCodeRequired }] }
    if (!formData.password || formData.password.length < MIN_PASSWORD_LENGTH) {
      return { success: false, errors: [{ field: "password", title: t.auth.errors.passwordTooShort }] }
    }
    if (formData.password !== formData.confirmPassword) {
      return { success: false, errors: [{ field: "confirmPassword", title: t.auth.errors.passwordsDoNotMatch }] }
    }
    const registerResponse = await register(signUpCode, formData.password);

    if (!registerResponse.success || !registerResponse.data) {
      return { success: false, errors: registerResponse.errors };
    }

    setAccessToken(registerResponse.data.accessToken);
    setRefreshToken(registerResponse.data.refreshToken);

    const userResponse = await getOwnUser();
    if (!userResponse.success || !userResponse.data) {
      return { success: false, errors: userResponse.errors };
    }

    setUser(userResponse.data);

    navigate("/");
    return { success: true }
  };

  // New invitation flow handler
  const handleInvitationRegisterSubmit = async (formData: Record<string, any>) => {
    setAccessToken("");
    setRefreshToken("");
    setUser(null);

    if (!signUpCode)
      return { success: false, errors: [{ title: t.auth.errors.invitationCodeRequired }] }

    if (!formData.password || formData.password.length < MIN_PASSWORD_LENGTH) {
      return { success: false, errors: [{ field: "password", title: t.auth.errors.passwordTooShort }] }
    }
    if (formData.password !== formData.confirmPassword) {
      return { success: false, errors: [{ field: "confirmPassword", title: t.auth.errors.passwordsDoNotMatch }] }
    }

    // Validate legalHoursPerWeek if provided
    if (formData.legalHoursPerWeek && !/^\d+$/.test(formData.legalHoursPerWeek)) {
      return { success: false, errors: [{ field: "legalHoursPerWeek", title: t.auth.errors.hoursPerWeekInvalid }] }
    }

    const registerResponse = await completeInvitation(signUpCode, {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      password: formData.password,
      company: formData.company,
      jobTitle: formData.jobTitle,
      legalHoursPerWeek: formData.legalHoursPerWeek ? parseInt(formData.legalHoursPerWeek, 10) : undefined,
    });

    if (!registerResponse.success || !registerResponse.data) {
      return { success: false, errors: registerResponse.errors };
    }

    setAccessToken(registerResponse.data.accessToken);
    setRefreshToken(registerResponse.data.refreshToken);

    const userResponse = await getOwnUser();
    if (!userResponse.success || !userResponse.data) {
      return { success: false, errors: userResponse.errors };
    }

    setUser(userResponse.data);

    navigate("/");
    return { success: true }
  };

  if (validationState.loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!validationState.valid && validationState.error) {
    const isExpired = validationState.errorType === 'expired';
    const isUsed = validationState.errorType === 'used';

    return (
      <div className="flex justify-center items-center h-full">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8">
            <div className="flex flex-col items-center text-center space-y-6">
              <img src={logo} alt="Queri" className="h-10" />
              <div className="rounded-full bg-muted p-4">
                {isExpired ? (
                  <Clock className="h-8 w-8 text-muted-foreground" />
                ) : (
                  <LinkIcon className="h-8 w-8 text-muted-foreground" />
                )}
              </div>
              <div className="space-y-2">
                <h1 className="text-xl font-semibold">
                  {isExpired ? t.auth.register.invalid.titleExpired : isUsed ? t.auth.register.invalid.titleUsed : t.auth.register.invalid.titleInvalid}
                </h1>
                <p className="text-muted-foreground">
                  {validationState.error}
                </p>
              </div>
              <p className="text-sm text-muted-foreground">
                {t.auth.register.invalid.contactHint}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Legacy flow - only password field
  if (validationState.isLegacyCode) {
    return (
      <div className="flex justify-center items-center h-full py-12 px-4">
        <Card className="w-full max-w-md shadow-lg border-0 bg-card/95 backdrop-blur">
          <CardContent className="p-8 md:p-12">
            <div className="flex flex-col items-center gap-3 mb-10">
              <img src={logo} alt="Queri" className="h-14 mb-4" />
              <h1 className="text-3xl md:text-4xl font-bold text-center">
                {t.auth.register.legacy.title}
              </h1>
              <p className="text-lg text-muted-foreground text-center">
                {t.auth.register.legacy.subtitle}
              </p>
            </div>
            <FormComponent onSubmit={handleLegacyRegisterSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">{t.auth.register.legacy.passwordLabel}</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  className="h-12 text-base"
                  placeholder={t.auth.register.legacy.passwordPlaceholder}
                />
                <p className="text-sm text-muted-foreground">{t.auth.register.legacy.passwordHint}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium">{t.auth.register.legacy.confirmPasswordLabel}</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  className="h-12 text-base"
                  placeholder={t.auth.register.legacy.passwordPlaceholder}
                />
              </div>
              <div className="pt-6">
                <Button type="submit" className="w-full h-12 text-base font-semibold">
                  {t.auth.register.legacy.submit}
                </Button>
              </div>
            </FormComponent>
          </CardContent>
        </Card>
      </div>
    );
  }

  // New invitation flow - full form
  return (
    <div className="flex justify-center items-center h-full py-12 px-4">
      <Card className="w-full max-w-3xl shadow-lg border-0 bg-card/95 backdrop-blur">
        <CardContent className="p-8 md:p-14">
          <div className="flex flex-col items-center gap-3 mb-10">
            <img src={logo} alt="Queri" className="h-14 mb-4" />
            <h1 className="text-3xl md:text-4xl font-bold text-center">
              {t.auth.register.invitation.title}
            </h1>
            <p className="text-lg text-muted-foreground text-center">
              {validationState.organizationName
                ? <>{t.auth.register.invitation.subtitleEarlyAdopter}</>
                : t.auth.register.invitation.subtitleDefault
              }
            </p>
          </div>
          <FormComponent
            onSubmit={handleInvitationRegisterSubmit}
            className="space-y-5"
            initialData={{
              email: validationState.email ?? '',
              company: validationState.organizationName ?? '',
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-sm font-medium">{t.auth.register.invitation.firstNameLabel} <span className="text-destructive">*</span></Label>
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  autoComplete="given-name"
                  className="h-12 text-base"
                  placeholder={t.auth.register.invitation.firstNamePlaceholder}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-sm font-medium">{t.auth.register.invitation.lastNameLabel} <span className="text-destructive">*</span></Label>
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  autoComplete="family-name"
                  className="h-12 text-base"
                  placeholder={t.auth.register.invitation.lastNamePlaceholder}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">{t.auth.register.invitation.emailLabel} <span className="text-destructive">*</span></Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className={`h-12 text-base${validationState.email ? ' bg-muted text-muted-foreground' : ''}`}
                placeholder={t.auth.register.invitation.emailPlaceholder}
                readOnly={!!validationState.email}
                required
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">{t.auth.register.invitation.passwordLabel} <span className="text-destructive">*</span></Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  className="h-12 text-base"
                  placeholder={t.auth.register.invitation.passwordPlaceholder}
                  required
                />
                <p className="text-sm text-muted-foreground">{t.auth.register.invitation.passwordHint}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium">{t.auth.register.invitation.confirmPasswordLabel} <span className="text-destructive">*</span></Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  className="h-12 text-base"
                  placeholder={t.auth.register.invitation.passwordPlaceholder}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="company" className="text-sm font-medium">{t.auth.register.invitation.companyLabel} <span className="text-destructive">*</span></Label>
                <Input
                  id="company"
                  name="company"
                  type="text"
                  autoComplete="organization"
                  required
                  className="h-12 text-base"
                  placeholder={t.auth.register.invitation.companyPlaceholder}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="jobTitle" className="text-sm font-medium">{t.auth.register.invitation.jobTitleLabel} <span className="text-destructive">*</span></Label>
                <Input
                  id="jobTitle"
                  name="jobTitle"
                  type="text"
                  autoComplete="organization-title"
                  required
                  className="h-12 text-base"
                  placeholder={t.auth.register.invitation.jobTitlePlaceholder}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="legalHoursPerWeek" className="text-sm font-medium">
                <span className="text-muted-foreground font-normal">{t.auth.register.invitation.legalHoursOptional}</span>{" "}
                {t.auth.register.invitation.legalHoursQuestion}
              </Label>
              <Input
                id="legalHoursPerWeek"
                name="legalHoursPerWeek"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder={t.auth.register.invitation.legalHoursPlaceholder}
                className="h-12 text-base"
              />
            </div>
            <div className="pt-6">
              <Button type="submit" className="w-full h-12 text-base font-semibold">
                {t.auth.register.invitation.submit}
              </Button>
            </div>
          </FormComponent>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterPage;
